import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AnnaAvatar } from '../../avatar/AnnaAvatar';
import { AgendaArc } from './AgendaArc';
import { OrbitCards } from './OrbitCards';
import { Satellites } from './Satellites';
import { SatelliteThread } from './SatelliteThread';
import { CaptionArea } from '../CaptionArea';
import { ProposalFocus } from '../ProposalFocus';
import { AlarmCard } from '../cards/AlarmCard';
import { ORBIT_GAP, type WallGeometry } from '../../../utils/orbitLayout';
import type { AgentId } from '../../../types/anna';
import type { AnnaStage } from '../useAnnaStage';
interface OrbitSceneProps {
  stage: AnnaStage;
  g: WallGeometry;
  nothingGoingOn: boolean;
  listening: boolean;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const FOCUS_BOTTOM = 150;

/** The living stage: Anna fills the room; cards, the agenda arc and agent satellites orbit her. */
export function OrbitScene({
  stage,
  g,
  nothingGoingOn,
  listening
}: OrbitSceneProps) {
  const [thread, setThread] = useState<{
    agent: AgentId;
    x: number;
    y: number;
  } | null>(null);
  const orbit = stage.view === 'stage';
  const approvals = stage.view === 'approvals';
  const wideCaption = approvals || stage.view === 'alarm';
  useEffect(() => {
    if (!orbit || stage.stopped) setThread(null);
  }, [orbit, stage.stopped]);
  const closeThread = useCallback(() => setThread(null), []);
  const hasAgenda = orbit && stage.cards.some(c => c.kind === 'vandaag');
  const captionW = wideCaption ? Math.min(820, g.w - 80) : Math.min(880, 2 * (g.R * ORBIT_GAP + 20) - 48);
  const captionTop = g.cy + g.R + (approvals ? 10 : 22);
  const focusW = Math.min(900, g.w - 80);
  const fromY = g.cy - (g.h - FOCUS_BOTTOM - 220);
  return <>
      <motion.div aria-hidden className="pointer-events-none absolute left-0 top-0" style={{
      width: g.canvas,
      height: g.canvas,
      zIndex: listening ? 13 : 1
    }} initial={false} animate={{
      x: g.cx - g.canvas / 2,
      y: g.cy - g.canvas / 2,
      scale: g.scale
    }} transition={{
      duration: 0.3,
      ease: EASE
    }} data-id="element-1482" data-mp="3pskwekf7k82b">
        <AnnaAvatar state={stage.avatarState} pulseKey={stage.pulseKey} data-id="element-1483" data-mp="36urjqe0jn575" />
      </motion.div>

      <AnimatePresence data-id="element-1484" data-mp="31xhk65jfxd5l">{hasAgenda && <AgendaArc key="agenda" cx={g.cx} cy={g.cy} R={g.R} data-id="element-1485" data-mp="31un04f4c9ucp" />}</AnimatePresence>

      {orbit && <Satellites cx={g.cx} cy={g.cy} R={g.R} stopped={stage.stopped} dim={stage.quiet} flash={stage.flash} openAgent={thread?.agent ?? null} onOpen={(agent, x, y) => setThread(t => t?.agent === agent ? null : {
      agent,
      x,
      y
    })} data-id="element-1486" data-mp="2ww20ohwwvepb" />}

      <OrbitCards g={g} cards={orbit ? stage.cards : []} onRemove={stage.removeCard} onApproved={stage.notifyApproved} onSound={stage.sound} data-id="element-1487" data-mp="3fow8mrw4a6k1" />

      <AnimatePresence data-id="element-1488" data-mp="299ltbhgjyfgq">
        {thread && <SatelliteThread key={thread.agent} agent={thread.agent} x={thread.x} y={thread.y} g={g} onClose={closeThread} data-id="element-1489" data-mp="249h8xgcix0l8" />}
      </AnimatePresence>

      <div className="absolute z-[14] flex flex-col items-center gap-6 text-center" style={{
      left: g.cx - captionW / 2,
      top: captionTop,
      width: captionW
    }} data-id="element-1490" data-mp="jshxfynlh2fx">
        <CaptionArea stage={stage} nothingGoingOn={nothingGoingOn} compact={approvals || stage.quiet} data-id="element-1491" data-mp="tobftynzvq5m" />
        <AnimatePresence data-id="element-1492" data-mp="yh4imgzwkof2">{stage.view === 'alarm' && <AlarmCard key="alarm" onAck={stage.ackAlarm} data-id="element-1493" data-mp="yi5swj93h6vq" />}</AnimatePresence>
      </div>

      <AnimatePresence data-id="element-1494" data-mp="13ewd9irxmomy">
        {approvals && <motion.div key="focus" className="absolute z-20" style={{
        left: g.cx - focusW / 2,
        bottom: FOCUS_BOTTOM,
        width: focusW
      }} exit={{
        opacity: 0,
        transition: {
          duration: 0.2
        }
      }} data-id="element-1495" data-mp="17g80xir8wzsi">
            <ProposalFocus pending={stage.pending} outcomes={stage.outcomes} onApprove={stage.approve} onReject={stage.reject} onEdit={() => stage.showToast('Aanpassen kan op je bureauscherm of telefoon.', 'meldt')} fromY={fromY} compact={g.h < 900} data-id="element-1496" data-mp="1he5pk7d0oyln" />
          </motion.div>}
      </AnimatePresence>
    </>;
}